import React from 'react';
import PropTypes from 'prop-types';

/**
 * Trend Indicator Component
 * Displays trend with icon, percentage, and optional comparison text
 * Used in KPIs and metric cards
 */
const TrendIndicator = ({ 
  value, 
  previousValue, 
  formatType = 'number',
  showComparison = true,
  size = 'medium' 
}) => {
  if (value === null || value === undefined || previousValue === null || previousValue === undefined) {
    return null;
  }

  const current = Number(value || 0);
  const previous = Number(previousValue || 0);
  
  // Calculate growth percentage
  let growth = 0;
  if (previous > 0) {
    growth = ((current - previous) / previous) * 100;
  } else if (current > 0) {
    growth = 100; // New data
  }

  const isPositive = growth > 0;
  const isNegative = growth < 0;
  const isNeutral = growth === 0;

  const sizeClasses = {
    small: 'trend-indicator-small',
    medium: 'trend-indicator-medium',
    large: 'trend-indicator-large'
  };

  const formatValue = (val) => {
    if (formatType === 'currency') {
      return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(val);
    }
    return new Intl.NumberFormat('en-US').format(val);
  };

  return (
    <div className={`trend-indicator ${sizeClasses[size]}`}>
      <div className={`trend-badge ${isPositive ? 'positive' : isNegative ? 'negative' : 'neutral'}`}>
        <i className={`bi ${isPositive ? 'bi-arrow-up' : isNegative ? 'bi-arrow-down' : 'bi-arrow-right'}`}></i>
        <span className="trend-value">{Math.abs(growth).toFixed(1)}%</span>
      </div>
      {showComparison && previous > 0 && (
        <div className="trend-comparison">
          <span className="trend-label">vs previous:</span>
          <span className="trend-previous">{formatValue(previous)}</span>
        </div>
      )}
    </div>
  );
};

TrendIndicator.propTypes = {
  value: PropTypes.number,
  previousValue: PropTypes.number,
  formatType: PropTypes.oneOf(['number', 'currency']),
  showComparison: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large'])
};

TrendIndicator.defaultProps = {
  value: null,
  previousValue: null,
  formatType: 'number',
  showComparison: true,
  size: 'medium'
};

export default React.memo(TrendIndicator);

